import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Film, Play, Download, X, Image as ImageIcon, Eye, ListPlus } from "lucide-react";

const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [search, setSearch] = useSearchParams();
    const [mediaFor, setMediaFor] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewIsVideo, setPreviewIsVideo] = useState(true);

    useEffect(() => { api.get("/brand/campaigns").then((r) => setCampaigns(r.data)); }, []);

    useEffect(() => {
        if (search.get("demo_paid")) {
            toast.success("Payment successful");
            setSearch({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBlob = async (campaign_id, a) => {
        const res = await api.get(`/brand/campaigns/${campaign_id}/asset/${a.asset_id}/download`, { responseType: "blob" });
        return URL.createObjectURL(new Blob([res.data], { type: a.content_type || "application/octet-stream" }));
    };
    const openPreview = async (campaign_id, a) => {
        try { setPreviewUrl(await fetchBlob(campaign_id, a)); setPreviewIsVideo(a.kind === "video"); }
        catch { toast.error("Could not load file"); }
    };
    const download = async (campaign_id, a) => {
        try {
            const url = await fetchBlob(campaign_id, a);
            const link = document.createElement("a");
            link.href = url; link.download = a.original_filename; link.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not download file"); }
    };

    const modalVideos = (mediaFor?.assets || []).filter((a) => a.kind === "video");
    const modalPhotos = (mediaFor?.assets || []).filter((a) => a.kind === "photo");
    const modalServices = mediaFor?.services || [];

    const AssetRow = ({ a, isVideo }) => (
        <div key={a.asset_id} className="flex items-center justify-between gap-3 border-2 border-yc-ink rounded-xl p-3 bg-white">
            <div className="flex items-center gap-2 min-w-0">{isVideo ? <Film className="w-4 h-4 shrink-0 text-yc-ink/50" /> : <ImageIcon className="w-4 h-4 shrink-0 text-yc-ink/50" />}<span className="text-sm truncate">{a.original_filename}</span></div>
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openPreview(mediaFor.campaign_id, a)} data-testid={`brand-view-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title={isVideo ? "Watch" : "View"}>{isVideo ? <Play className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                <button onClick={() => download(mediaFor.campaign_id, a)} data-testid={`brand-download-${a.asset_id}`} className="p-2 rounded-lg hover:bg-yc-cream" title="Download"><Download className="w-4 h-4" /></button>
            </div>
        </div>
    );

    return (
        <DashboardShell role="brand" title="Campaigns" actions={
            <Link to="/brand/campaigns/new" data-testid="campaigns-new-btn" className="yc-btn-primary text-sm py-2 px-4">New campaign</Link>
        }>
            {previewUrl && (
                <div className="fixed inset-0 z-[60] bg-yc-ink/80 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}>
                    <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }} className="absolute -top-10 right-0 text-white"><X className="w-6 h-6" /></button>
                        {previewIsVideo
                            ? <video src={previewUrl} controls autoPlay className="w-full rounded-2xl border-2 border-white" data-testid="brand-video-preview" />
                            : <img src={previewUrl} alt="preview" className="w-full max-h-[80vh] object-contain rounded-2xl border-2 border-white" data-testid="brand-photo-preview" />}
                    </div>
                </div>
            )}
            {mediaFor && (
                <div className="fixed inset-0 z-50 bg-yc-ink/60 flex items-center justify-center p-4" onClick={() => setMediaFor(null)} data-testid="brand-media-modal">
                    <div className="yc-card p-6 sm:p-8 bg-yc-cream w-full max-w-lg relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setMediaFor(null)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
                        <h3 className="font-heading text-xl font-black tracking-tighter">{mediaFor.title}</h3>
                        {modalVideos.length > 0 && (
                            <div className="mt-4">
                                <div className="yc-overline flex items-center gap-1.5"><Film className="w-3.5 h-3.5" /> Videos</div>
                                <div className="mt-2 space-y-2">{modalVideos.map((a) => <AssetRow key={a.asset_id} a={a} isVideo />)}</div>
                            </div>
                        )}
                        {modalPhotos.length > 0 && (
                            <div className="mt-5">
                                <div className="yc-overline flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Photography</div>
                                <div className="mt-2 space-y-2">{modalPhotos.map((a) => <AssetRow key={a.asset_id} a={a} isVideo={false} />)}</div>
                            </div>
                        )}
                        {modalServices.length > 0 && (
                            <div className="mt-5">
                                <div className="yc-overline flex items-center gap-1.5"><ListPlus className="w-3.5 h-3.5" /> Services</div>
                                <div className="mt-2 space-y-2">
                                    {modalServices.map((s) => (
                                        <div key={s.service_id} className="flex items-center justify-between gap-3 border-2 border-yc-ink rounded-xl p-3 bg-white" data-testid={`brand-service-${s.service_id}`}>
                                            <span className="text-sm font-semibold">{s.description}</span>
                                            {s.price && <span className="text-sm text-yc-blue font-bold shrink-0">{s.price}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {modalVideos.length === 0 && modalPhotos.length === 0 && modalServices.length === 0 && (
                            <div className="text-sm text-yc-ink/60 py-3 mt-2">Nothing shared yet.</div>
                        )}
                    </div>
                </div>
            )}
            <div className="yc-card overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                    <thead className="bg-yc-cream border-b-2 border-yc-ink">
                        <tr>
                            <th className="text-left p-4">Title</th>
                            <th className="text-left p-4">Videos</th>
                            <th className="text-left p-4">Status</th>
                            <th className="text-left p-4">Payment</th>
                            <th className="text-left p-4">Workspace</th>
                        </tr>
                    </thead>
                    <tbody>
                        {campaigns.map((c) => {
                            const assets = c.assets || [];
                            const svcs = c.services || [];
                            const hasExtras = assets.length > 0 || svcs.length > 0;
                            return (
                            <tr key={c.campaign_id} className="border-b border-yc-ink/10" data-testid={`campaign-${c.campaign_id}`}>
                                <td className="p-4 font-semibold">{c.title}</td>
                                <td className="p-4">
                                    {c.videos_count || c.package_id || "—"}
                                    {(c.addons || []).length > 0 && <span className="yc-badge bg-yc-pink text-white ml-2 text-[10px]">+{c.addons.length} add-on{c.addons.length === 1 ? "" : "s"}</span>}
                                </td>
                                <td className="p-4"><span className="yc-badge bg-yc-cream">{c.status}</span></td>
                                <td className="p-4"><span className="yc-badge bg-white">{c.payment_status}</span></td>
                                <td className="p-4">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {hasExtras && (
                                            <button onClick={() => setMediaFor(c)} data-testid={`view-media-${c.campaign_id}`} className="yc-btn-secondary text-xs py-1.5 px-3 inline-flex items-center">
                                                <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Files & services{assets.length + svcs.length > 0 ? ` (${assets.length + svcs.length})` : ""}
                                            </button>
                                        )}
                                        {c.placement_id ? (
                                            <Link to={`/placement/${c.placement_id}`} data-testid={`workspace-${c.campaign_id}`} className="yc-btn-primary text-xs py-1.5 px-3 inline-flex items-center">
                                                <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Open workspace
                                            </Link>
                                        ) : (
                                            !hasExtras && <span className="text-xs text-yc-ink/50">Awaiting match</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );})}
                        {campaigns.length === 0 && (
                            <tr><td colSpan="5" className="p-8 text-center text-yc-ink/60">No campaigns yet.</td></tr>
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        </DashboardShell>
    );
};

export default Campaigns;
